import { useState } from 'react';
import { DateTime } from 'luxon';
import { Button, Calendar, Input, Tooltip } from '@flixbus/honeycomb-react';
import { Icon, IconCalendar, IconClose } from '@flixbus/honeycomb-icons-react';

import { useVoucherFormContext } from '../../Context';
import { BlackoutPeriodRule } from '../../../../types/Rules';

const BlackoutTo = ({ index }: { index: number }) => {
  const {
    formState: {
      payload: { rules },
    },
    handleRuleChange,
  } = useVoucherFormContext();

  const [calendarOpen, setCalendarOpen] = useState(false);

  // Get the specific blackout period rule for this index
  const ruleValue = rules.find(
    r => r.type === 'blackout_period' && (r as BlackoutPeriodRule).id === `blackout_${index}`,
  ) as BlackoutPeriodRule | undefined;

  const dateObject = ruleValue?.end ? DateTime.fromISO(ruleValue.end) : null;

  // Set minimum date to the start date (same day or later)
  const startDateObject = ruleValue?.start ? DateTime.fromISO(ruleValue.start) : null;
  const minDate = startDateObject
    ? startDateObject.setLocale('de').toFormat('yyyy-MM-dd')
    : new Date().toISOString().split('T')[0];

  const disableDate = (date: Date) => {
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0);
    return date < min;
  };

  const calendarEndDate = new Date();
  calendarEndDate.setFullYear(calendarEndDate.getFullYear() + 5);

  const handleClear = () => {
    const ruleId = `blackout_${index}`;

    // If there's no start date, remove the rule entirely
    if (!ruleValue?.start) {
      handleRuleChange({ type: 'blackout_period', id: ruleId } as BlackoutPeriodRule, true, ruleId);
    } else {
      // Keep the rule but remove the end date
      handleRuleChange(
        {
          type: 'blackout_period',
          id: ruleId,
          start: ruleValue.start,
        } as BlackoutPeriodRule,
        false,
        ruleId,
      );
    }
  };

  return (
    <Tooltip
      id={`calendar-blackout-to-tooltip-${index}`}
      active={calendarOpen}
      disableInternalHandlers
      onToggle={() => setCalendarOpen(false)}
      position='bottom'
      alignment='start'
      size='content-fit'
      wrapAround='block'
      extraClasses='calendar-tooltip'
      content={
        <Calendar
          id={`calendar-blackout-to-${index}`}
          appearance='compact'
          startDate={new Date(minDate)}
          endDate={calendarEndDate}
          selected={dateObject?.isValid ? dateObject.toJSDate() : undefined}
          showCurrentDate
          handleSelect={date => {
            const ruleId = `blackout_${index}`;
            // Update or create the rule with the new end date
            handleRuleChange(
              {
                type: 'blackout_period',
                id: ruleId,
                start: ruleValue?.start,
                end: DateTime.fromJSDate(date)
                  .endOf('day')
                  .startOf('second')
                  .toISO({ includeOffset: true, suppressMilliseconds: true }) as string,
              } as BlackoutPeriodRule,
              false,
              ruleId,
            );
            setCalendarOpen(false);
          }}
          disableDayCondition={disableDate}
        />
      }
    >
      <Input
        name={`rule-blackout-to-input-${index}`}
        id={`rule-blackout-to-input-${index}`}
        label={<>To</>}
        type='text'
        readOnly
        required
        onClick={() => setCalendarOpen(prev => !prev)}
        value={dateObject?.isValid ? dateObject.toFormat('dd.MM.yyyy') : ''}
        placeholder='DD.MM.YYYY'
        iconLeft={
          <Button aria-label='Open calendar' onClick={() => setCalendarOpen(prev => !prev)}>
            <Icon appearance='primary' InlineIcon={IconCalendar} />
          </Button>
        }
        iconRight={
          dateObject ? (
            <Button aria-label='Clear end date field' onClick={handleClear}>
              <Icon appearance='primary' InlineIcon={IconClose} />
            </Button>
          ) : undefined
        }
      />
    </Tooltip>
  );
};

export default BlackoutTo;
